import React, { useState } from "react";
import Navbar from "../components/Navbar";
import api, { apiError } from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Music, UploadCloud, QrCode, Loader2, FileSpreadsheet, Download, X, Mail } from "lucide-react";
import { fetchBuildId, shortBuildId } from "../lib/version";

const PLATFORMS = [
  { key: "deezer", label: "Deezer" },
  { key: "spotify", label: "Spotify" },
  { key: "apple_music", label: "Apple Music" },
  { key: "youtube_music", label: "YouTube Music" },
  { key: "bandcamp", label: "Bandcamp" },
];

export default function Settings() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [platform, setPlatform] = useState(user?.preferred_platform || "deezer");
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [smsAvailable, setSmsAvailable] = useState(false);
  const [smsStep, setSmsStep] = useState(false);
  const [phone, setPhone] = useState("");
  const [appVersion, setAppVersion] = useState("…");
  const [previewData, setPreviewData] = useState(null); // { bands, events, concerts, skipped_concerts, file }
  const [importing, setImporting] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ subject: "", message: "", category: "question" });
  const [contactSending, setContactSending] = useState(false);

  const sendContact = async () => {
    if (!contactForm.subject.trim() || !contactForm.message.trim()) return;
    setContactSending(true);
    try {
      await api.post("/contact", contactForm);
      toast.success(t("contact_sent"));
      setContactOpen(false);
      setContactForm({ subject: "", message: "", category: "question" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    setContactSending(false);
  };

  React.useEffect(() => {
    fetchBuildId().then((id) => setAppVersion(shortBuildId(id)));
  }, []);

  React.useEffect(() => {
    api.get("/auth/mfa/config").then(({ data }) => setSmsAvailable(data.sms_available)).catch(() => {});
  }, []);

  const input = "w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none";
  const label = "label-caps text-xs text-zinc-400";

  const savePrefs = async () => {
    const { data } = await api.put("/auth/preferences", { preferred_platform: platform });
    setUser(data);
    toast.success(t("preferences_saved"));
  };

  const changePw = async () => {
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      setCur(""); setNext("");
      toast.success(t("password_changed"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  const startMfa = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setMfaSetup(data);
    } finally { setBusy(false); }
  };

  const enableMfa = async () => {
    try {
      await api.post("/auth/mfa/enable", { code: mfaCode });
      setMfaSetup(null); setMfaCode("");
      setUser({ ...user, mfa_enabled: true });
      toast.success(t("mfa_on"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  const disableMfa = async () => {
    try {
      await api.post("/auth/mfa/disable", { code: mfaCode });
      setMfaCode(""); setSmsStep(false);
      setUser({ ...user, mfa_enabled: false, mfa_method: null });
      toast.success(t("mfa_off"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  const startSms = async () => {
    try {
      await api.post("/auth/mfa/sms/setup", { phone });
      setSmsStep(true);
      toast.success("SMS →");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  const enableSms = async () => {
    try {
      await api.post("/auth/mfa/sms/enable", { code: mfaCode });
      setSmsStep(false); setMfaCode("");
      setUser({ ...user, mfa_enabled: true, mfa_method: "sms" });
      toast.success(t("mfa_on"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  const sendSmsCode = async () => {
    try { await api.post("/auth/mfa/sms/send"); toast.success("SMS →"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await api.post("/import", {
        bands: json.bands || [], events: json.events || [], concerts: json.concerts || [],
      });
      toast.success(`${t("import_done")} — ${data.imported.bands} ${t("bands_count")}, ${data.imported.concerts} ${t("concert_count")}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    e.target.value = "";
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/file?preview=true", fd);
      setPreviewData({ ...data.preview, file });
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    e.target.value = "";
  };

  const confirmImport = async () => {
    if (!previewData?.file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", previewData.file);
      const { data } = await api.post("/import/file", fd);
      const im = data.imported;
      toast.success(`${t("import_done")} — ${im.bands} ${t("bands_count")}, ${im.events} ${t("events")}, ${im.concerts} ${t("concert_count")}`);
      setPreviewData(null);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    setImporting(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "metalvault-modele.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <h1 className="heading text-4xl font-bold text-white mb-2">{t("settings")}</h1>

        <Card icon={Music} title={t("account")}>
          <label className={label}>{t("preferred_platform")}</label>
          <select data-testid="pref-platform-select" value={platform} onChange={(e) => setPlatform(e.target.value)} className={input}>
            {PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <button data-testid="save-prefs-btn" onClick={savePrefs} className="mt-3 px-5 py-2.5 bg-primary text-primary-foreground label-caps rounded-sm font-semibold hover:bg-yellow-400">{t("save_preferences")}</button>
        </Card>

        <Card icon={KeyRound} title={t("change_password")}>
          <div className="space-y-3">
            <div><label className={label}>{t("current_password")}</label><input data-testid="current-pw" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={input} /></div>
            <div><label className={label}>{t("new_password")}</label><input data-testid="new-pw" type="password" value={next} onChange={(e) => setNext(e.target.value)} className={input} /></div>
            <button data-testid="change-pw-btn" onClick={changePw} disabled={!cur || !next} className="px-5 py-2.5 border border-zinc-700 text-white label-caps rounded-sm hover:border-primary hover:text-primary disabled:opacity-50">{t("change_password")}</button>
          </div>
        </Card>

        <Card icon={ShieldCheck} title={t("mfa")}>
          <p className="text-sm mb-3">
            <span className={`label-caps text-xs px-2 py-1 rounded-sm ${user?.mfa_enabled ? "bg-green-900/40 text-green-400" : "bg-zinc-800 text-zinc-400"}`} data-testid="mfa-status">
              {user?.mfa_enabled ? t("mfa_on") : t("mfa_off")}
            </span>
          </p>
          {!user?.mfa_enabled && !mfaSetup && !smsStep && (
            <div className="flex flex-wrap gap-2">
              <button data-testid="start-mfa-btn" onClick={startMfa} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground label-caps rounded-sm font-semibold hover:bg-yellow-400">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />} {t("enable_mfa")} (TOTP)
              </button>
              {smsAvailable && (
                <div className="flex gap-2 items-center">
                  <input data-testid="sms-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33..." className={input + " w-40"} />
                  <button data-testid="start-sms-btn" onClick={startSms} disabled={!phone} className="px-4 py-2.5 border border-zinc-700 text-white label-caps rounded-sm hover:border-primary hover:text-primary disabled:opacity-50">SMS</button>
                </div>
              )}
            </div>
          )}
          {mfaSetup && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">{t("scan_qr")}</p>
              <img data-testid="mfa-qr" src={mfaSetup.qr_data_uri} alt="QR" className="w-44 h-44 bg-white p-2 rounded-sm" />
              <p className="text-xs text-zinc-500">{t("mfa_secret_manual")} <code className="text-zinc-300">{mfaSetup.secret}</code></p>
              <input data-testid="mfa-enable-code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={t("enter_code")} className={input} />
              <button data-testid="enable-mfa-btn" onClick={enableMfa} className="px-5 py-2.5 bg-primary text-primary-foreground label-caps rounded-sm font-semibold hover:bg-yellow-400">{t("verify")}</button>
            </div>
          )}
          {smsStep && !user?.mfa_enabled && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">Code SMS envoyé à {phone}</p>
              <input data-testid="sms-enable-code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={t("enter_code")} className={input} />
              <button data-testid="enable-sms-btn" onClick={enableSms} className="px-5 py-2.5 bg-primary text-primary-foreground label-caps rounded-sm font-semibold hover:bg-yellow-400">{t("verify")}</button>
            </div>
          )}
          {user?.mfa_enabled && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500 label-caps">{user?.mfa_method === "sms" ? `SMS · ${user?.mfa_phone || ""}` : "TOTP"}</p>
              {user?.mfa_method === "sms" && (
                <button data-testid="send-sms-btn" onClick={sendSmsCode} className="text-sm text-primary hover:underline">{t("enter_code")} →</button>
              )}
              <input data-testid="mfa-disable-code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={t("enter_code")} className={input} />
              <button data-testid="disable-mfa-btn" onClick={disableMfa} className="px-5 py-2.5 border border-red-900 text-red-400 label-caps rounded-sm hover:bg-red-950/40">{t("disable_mfa")}</button>
            </div>
          )}
        </Card>

        <Card icon={UploadCloud} title={t("import_directory")}>
          <p className="text-sm text-zinc-400 mb-3">{t("import_desc")}</p>
          <label data-testid="import-btn" className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 text-white label-caps rounded-sm hover:border-primary hover:text-primary cursor-pointer">
            <UploadCloud size={16} /> {t("import")}
            <input type="file" accept="application/json,.json" className="hidden" onChange={importFile} data-testid="import-file-input" />
          </label>
        </Card>

        <Card icon={FileSpreadsheet} title={t("import_excel")}>
          <p className="text-sm text-zinc-400 mb-3">{t("import_excel_desc")}</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="download-template-btn" onClick={downloadTemplate}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 text-zinc-200 label-caps rounded-sm hover:border-primary hover:text-primary">
              <Download size={16} /> {t("download_template")}
            </button>
            <label data-testid="import-excel-btn" className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 text-white label-caps rounded-sm hover:border-primary hover:text-primary cursor-pointer">
              <FileSpreadsheet size={16} /> {t("import_file")}
              <input type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={importExcel} data-testid="import-excel-file-input" />
            </label>
          </div>
        </Card>

        <div className="mt-10 pt-6 border-t border-zinc-800 text-center">
          <button data-testid="contact-link" onClick={() => setContactOpen(true)}
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-primary transition-colors">
            <Mail size={16} /> {t("contact_us")}
          </button>
        </div>

        <p data-testid="app-version" className="text-center text-xs text-zinc-600 mt-8">
          MetalVault · {t("app_version")} {appVersion}
        </p>
      </main>

      {previewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !importing && setPreviewData(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading text-2xl text-white">{t("import_preview_title")}</h3>
              <button onClick={() => !importing && setPreviewData(null)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">{t("import_preview_desc")}</p>
            <div className="space-y-2 mb-5">
              <div className="flex justify-between border border-zinc-800 rounded-sm px-3 py-2.5"><span className="text-zinc-300 text-sm">{t("bands_count")}</span><span data-testid="preview-bands" className="text-primary font-semibold">{previewData.bands}</span></div>
              <div className="flex justify-between border border-zinc-800 rounded-sm px-3 py-2.5"><span className="text-zinc-300 text-sm">{t("events")}</span><span data-testid="preview-events" className="text-primary font-semibold">{previewData.events}</span></div>
              <div className="flex justify-between border border-zinc-800 rounded-sm px-3 py-2.5"><span className="text-zinc-300 text-sm">{t("concert_count")}</span><span data-testid="preview-concerts" className="text-primary font-semibold">{previewData.concerts}</span></div>
              {previewData.skipped_concerts > 0 && (
                <p data-testid="preview-skipped" className="text-xs text-amber-400 pt-1">{t("import_skipped")} : {previewData.skipped_concerts}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button data-testid="cancel-import-btn" onClick={() => setPreviewData(null)} disabled={importing}
                className="flex-1 border border-zinc-700 text-zinc-300 label-caps py-3 rounded-sm hover:border-zinc-500 disabled:opacity-50">{t("cancel") || "Annuler"}</button>
              <button data-testid="confirm-import-btn" onClick={confirmImport} disabled={importing || (previewData.bands + previewData.events + previewData.concerts === 0)}
                className="flex-1 bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {importing && <Loader2 size={16} className="animate-spin" />} {t("import_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {contactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !contactSending && setContactOpen(false)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="heading text-2xl text-white flex items-center gap-2"><Mail size={20} className="text-primary" /> {t("contact_title")}</h3>
              <button onClick={() => !contactSending && setContactOpen(false)} className="text-zinc-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">{t("contact_desc")}</p>
            <div className="space-y-3">
              <div>
                <label className="label-caps text-xs text-zinc-500">{t("contact_category")}</label>
                <select data-testid="contact-category" value={contactForm.category} onChange={(e) => setContactForm({ ...contactForm, category: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-zinc-200 focus:border-primary focus:outline-none">
                  <option value="question">{t("cat_question")}</option>
                  <option value="bug">{t("cat_bug")}</option>
                  <option value="suggestion">{t("cat_suggestion")}</option>
                </select>
              </div>
              <div>
                <label className="label-caps text-xs text-zinc-500">{t("contact_subject")}</label>
                <input data-testid="contact-subject" value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label className="label-caps text-xs text-zinc-500">{t("contact_message")}</label>
                <textarea data-testid="contact-message" rows={5} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
              </div>
              <button data-testid="contact-send" onClick={sendContact} disabled={contactSending || !contactForm.subject.trim() || !contactForm.message.trim()}
                className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {contactSending && <Loader2 size={16} className="animate-spin" />} {t("contact_send")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
      <h2 className="flex items-center gap-2 label-caps text-sm text-zinc-300 mb-4 border-b border-zinc-800 pb-3">
        <Icon size={18} className="text-primary" /> {title}
      </h2>
      {children}
    </div>
  );
}
